/**
 * Playground Types
 * Image generation using /v1/images API
 */

// ============================================================================
// Dynamic Model Info (from API)
// ============================================================================

/**
 * Model information from backend API
 * Matches the ImageModelInfo response from /v1/images/models
 * Config-driven: all data comes from image_models.yaml
 */
export interface ImageModelInfo {
  model_id: string;
  display_name: string;
  vendor: string; // "google" | "openai" | "bytedance"
  provider: string; // legacy alias for vendor
  supports_generate: boolean;
  supports_edit: boolean;
  supports_fuse: boolean;
  sizes: string[];
  default_size: string;
  aspect_ratios: string[];
  default_aspect_ratio: string | null;
  quality_options: string[];
  default_quality: string | null;
  supports_web_search: boolean;
  supports_transparency: boolean;
  max_images_edit: number;
  max_images_fuse: number;
  max_n: number;
  max_prompt_length: number;
  output_formats: string[];
  default_output_format: string;
  response_format: string; // "base64" | "url"
  extra_params: string[];
  cost_per_image: number; // ¥RMB per image
}

/**
 * Vendor names for grouping models (from YAML vendor field)
 */
export type ImageVendor = "google" | "openai" | "bytedance";

/**
 * Vendor display info — used by ModelSelector for tab grouping
 */
export const VENDOR_INFO: Record<
  string,
  { label: string; description: string }
> = {
  google: { label: "Gemini", description: "Google Gemini 图像模型" },
  openai: { label: "OpenAI", description: "GPT Image / DALL-E" },
  bytedance: { label: "Seedream", description: "豆包即梦图像模型" },
};

/** @deprecated Use VENDOR_INFO instead */
export const PROVIDER_INFO = VENDOR_INFO;

// ============================================================================
// Legacy Static Types (for backward compatibility)
// ============================================================================

// Image Model Options (legacy - use ImageModelInfo instead)
export type ImageModel =
  | "gemini-2.5-flash-image"
  | "gemini-3-pro-image-preview";

export interface ImageModelOption {
  value: ImageModel;
  label: string;
  maxFuseImages: number;
}

export const IMAGE_MODELS: ImageModelOption[] = [
  { value: "gemini-2.5-flash-image", label: "FLASH", maxFuseImages: 4 },
  { value: "gemini-3-pro-image-preview", label: "PRO", maxFuseImages: 14 },
];

// Image Size Options
export type ImageSize = "1K" | "2K" | "4K";

export interface ImageSizeOption {
  value: ImageSize;
  label: string;
  models: string[]; // Which model labels support this size
}

export const IMAGE_SIZES: ImageSizeOption[] = [
  { value: "1K", label: "1K (1024px)", models: ["FLASH", "PRO"] },
  { value: "2K", label: "2K (2048px)", models: ["PRO"] },
  { value: "4K", label: "4K (4096px)", models: ["PRO"] },
];

// Aspect Ratio Options
export type AspectRatio =
  | "1:1"
  | "3:4"
  | "4:3"
  | "9:16"
  | "16:9"
  | "1:2"
  | "2:1";

export interface AspectRatioOption {
  value: AspectRatio;
  label: string;
}

export const ASPECT_RATIOS: AspectRatioOption[] = [
  { value: "1:1", label: "1:1 (正方形)" },
  { value: "3:4", label: "3:4 (肖像)" },
  { value: "4:3", label: "4:3 (风景)" },
  { value: "9:16", label: "9:16 (手机竖屏)" },
  { value: "16:9", label: "16:9 (宽屏)" },
  { value: "1:2", label: "1:2 (长图)" },
  { value: "2:1", label: "2:1 (横幅)" },
];

// Response Modality Options (legacy - kept for backward compatibility)
export type ResponseModality = "IMAGE" | "TEXT_AND_IMAGE";

export interface ResponseModalityOption {
  value: ResponseModality;
  label: string;
}

export const RESPONSE_MODALITIES: ResponseModalityOption[] = [
  { value: "IMAGE", label: "仅图像" },
  { value: "TEXT_AND_IMAGE", label: "图像+文字" },
];

// ============================================================================
// Image Generation Config
// ============================================================================

// Dynamic config using model_id from API
export interface ImageGenerationConfig {
  model: string; // model_id from API
  size: string;
  aspect_ratio: string;
  quality: string | null;
  output_format: string;
  n: number;
  background: string | null;
  web_search: boolean;
  // Legacy aliases for backward compatibility
  image_size?: string; // alias for size
  response_modality?: ResponseModality; // legacy, no longer used
}

export const DEFAULT_CONFIG: ImageGenerationConfig = {
  model: "gemini-3.1-flash-image-preview", // Nano Banana 2
  size: "1K",
  aspect_ratio: "3:4",
  quality: null,
  output_format: "png",
  n: 1,
  background: null,
  web_search: false,
  image_size: "1K", // legacy alias
  response_modality: "IMAGE", // legacy
};

// API Request/Response Types
export interface GenerateImageRequest {
  prompt: string;
  model?: string;
  size?: string;
  aspect_ratio?: string;
  quality?: string;
  output_format?: string;
  n?: number;
  background?: string;
  web_search?: boolean;
  // For edit mode
  image_data?: string; // Base64 encoded image
  source_image?: string; // Base64 or URL for edit (backend field name)
  parent_id?: string;
  // For fuse mode
  source_images?: string[]; // Array of base64 or URLs for fusion (backend field name)
  images?: string[]; // Legacy alias
  parent_ids?: string[];
  // Legacy properties for backward compatibility
  image_size?: string; // alias for size
  response_modality?: ResponseModality; // legacy, ignored by backend
  reference_image?: string; // legacy alias for image_data
  reference_images?: string[]; // legacy alias for images
  edit_mode?: "inpaint" | "outpaint" | "variation"; // legacy
  mask_image?: string; // legacy
  fuse_weights?: number[]; // legacy
  // Async mode
  sync?: boolean; // false = return task_id immediately, poll for status
}

export interface GenerateImageResponse {
  task_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  image_url?: string;
  image_base64?: string;
  text_response?: string;
  error?: string;
  created_at: string;
  // Async mode fields
  estimated_time_ms?: number;
  elapsed_ms?: number;
  temp_url?: string;
}

// Task Types
export type TaskType = "generate" | "edit" | "fuse" | "lucky_charm";

// ============================================================================
// Lucky Charm Types
// ============================================================================

/**
 * Lucky Charm phrase input mode (for UI, determines how to get the phrase)
 * - manual: User directly inputs the phrase
 * - agent: Agent generates the phrase
 * - llm: LLM generates the phrase
 */
export type LuckyCharmInputMode = "manual" | "agent" | "llm";

/**
 * Emotion trend options
 */
export type EmotionTrend = "positive" | "negative" | "neutral" | "mixed";

/**
 * Lucky Charm generation request (matches backend API)
 * Always uses mode='direct' with generated/input phrase
 */
export interface LuckyCharmRequest {
  mode: "direct"; // Always use direct mode with phrase
  phrase: string; // The fortune phrase to render
  // Theme/pack ID for background selection
  theme_id?: string;
  // Specific background image ID (full path or filename)
  background_image_id?: string;
  font_id?: number;
  // Output format: 'jpeg' or 'png'
  output_format?: string;
  // Language preference: 'zh', 'en', or 'auto'
  language?: string;
}

/**
 * Lucky Charm generation response
 */
export interface LuckyCharmResponse {
  task_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  image_url: string;
  phrase: string;
  mode: string;
  chart_id?: string;
  object_name?: string;
  error?: string;
}

// Backend TaskStatusResponse format
export interface ImageTask {
  task_id: string;
  task_type: TaskType;
  status:
    | "pending"
    | "processing"
    | "completed"
    | "failed"
    | "saved"
    | "expired";
  prompt?: string;
  model?: string;
  // Progress (for async)
  progress?: number;
  elapsed_ms?: number;
  estimated_time_ms?: number;
  // Result (when completed)
  temp_url?: string;
  generation_time_ms?: number;
  width?: number;
  height?: number;
  // Error
  error?: string;
  // Timestamps
  created_at?: string;
  completed_at?: string;
  // Alias for convenience (mapped from task_id)
  id: string;
  type: TaskType;
  // Legacy config structure for backward compatibility
  config: { model?: string };
  result?: {
    image_url?: string;
  };
}

// Library Item
export interface LibraryItem {
  id: string;
  filename: string;
  url?: string;
  thumbnail_url?: string;
  category?: string;
  content_type?: string;
  size?: number;
  metadata?: Record<string, unknown>;
  created_at: string;
}

// Prompt Generation Mode
export type PromptGenerationMode = "agent" | "raw";

// Prompt Generation Request (supports both Agent and Raw modes)
export interface PromptGenerationRequest {
  user_input: string;
  operation?: "generate" | "edit" | "fuse";
  style_hints?: string;
  context?: string;
  language?: "zh" | "en";
  include_negative_prompt?: boolean;
  max_length?: number;
  // Agent Mode
  agentType?: string;
  output_schema_key?: string; // Output schema key from agent config
  // Raw LLM Mode
  model?: string;
  temperature?: number;
  system_prompt?: string;
  // Legacy alias
  operationMode?: "generate" | "edit" | "fuse";
}

export interface PromptGenerationResponse {
  prompt: string;
  negative_prompt?: string;
  suggestions?: string[];
  recommended_model?: string;
  recommended_params?: Record<string, unknown>;
  mode: PromptGenerationMode;
  agent_used?: string;
  model_used?: string;
  tokens_used?: number;
  // Transparency fields (raw mode only)
  system_prompt?: string;
  user_prompt?: string;
  // Schema fields (Agent Mode with output_schema_key)
  schema_applied?: boolean;
  extracted_data?: Record<string, unknown>;
}

// Template response for transparency (shows what prompts will be used)
export interface PromptAssistTemplateResponse {
  system_prompts: Record<string, Record<"zh" | "en", string>>;
  user_templates: Record<string, Record<"zh" | "en", string>>;
}
